import { useCallback, useMemo } from "react"
import { CreateTestTakerContext } from "../CreateTestTakerContext"
import { useContext } from "react"
import { useSelector } from "react-redux"
import { createAffiliationHooks } from "pages/School/CreateAffiliation"
import { useMutation } from "@tanstack/react-query"
import { searchTestTakerByTzFetch } from "store/middlewares/testTakers.crud"
import { useDispatch } from "react-redux"
import { initialize, submit } from "redux-form"
import { TestTakerVM } from "models/VM"
import { testTakerHooks, validate as validateTestTaker } from ".."

const useFields = () => {
    const formValues = useSelector(state => state.form["test_taker"]?.values)
    const { filled_fields } = formValues || {}
    const { setTzApproved, schools, schoolClasses, grades } = useContext(CreateTestTakerContext)
    const { languages } = useSelector(state => state.static)

    const affiliationField = createAffiliationHooks.useAffiliationField(schools)
    const resetForm = testTakerHooks.useRestForm()

    const dispatch = useDispatch()

    const { mutateAsync: searchTestTakerByTz, isPending: searchTestTakerPending } = useMutation({
        mutationFn: () => searchTestTakerByTzFetch(formValues?.tz),
        onSuccess: (res) => {
            let { data } = res

            // אישור תעודת הזהות מאפשרת להמשיך למלא את טופס התלמיד
            setTzApproved(true)

            // את התלמיד כבר קיים במערכת, אנחנו מעדכנים בטופס את כל השדות של התלמיד
            if (data?.id) {
                const { learningUnitType, learningUnit, home_affiliation, home_grade, home_class } = formValues || {}
                data = {
                    ...data,
                    home_affiliation: home_affiliation,
                    home_grade: home_grade,
                    home_class: home_class
                }
                resetForm()
                const testTaker = new TestTakerVM(data, learningUnitType, learningUnit);
                dispatch(initialize('test_taker', testTaker))
            }
        }
    })

    const searchByTz = useCallback(() => {
        const errors = validateTestTaker(formValues || {})
        // בדיקת ולידצייה של תעודת הזהות, והצגת שגיאת טופס
        if (errors.tz)
            dispatch(submit("test_taker")) // השאירי כדי להציג שגיאות
        // במקרה שתעודה תקינה הבדיקה עוברת לשרת
        else searchTestTakerByTz()
    }, [formValues])

    const initialFields = useMemo(() => [{
        name: "tz",
        component: "input",
        placeholder: "ת.ז",
        className: "_tz",
        required: true,
        fullWidth: true,
        onKeyDown: searchByTz
    },
    {
        name: "search_test_taker",
        component: "button",
        label: "אישור",
        // type: "submit",
        color: "dark",
        disabled: !formValues?.tz,
        width: 150,
        className: "solid-round",
        action: searchByTz,
        loading: searchTestTakerPending
    }
    ], [formValues, searchTestTakerPending])

    const fields = useMemo(() =>
        [
            {
                name: "tz",
                placeholder: "תעודת זהות",
                component: "input",
                className: "_child__tz",
                required: true
            },
            {
                name: "dob",
                placeholder: "תאריך לידה",
                component: "datePicker",
                systemUI: true,
                className: "_child__db",
                isLocked: filled_fields?.dob
            },
            {
                name: "first_name",
                component: "input",
                placeholder: "שם פרטי",
                className: "_child__first_name",
                required: true
            },
            {
                name: "last_name",
                component: "input",
                placeholder: "שם משפחה",
                className: "_child__last_name",
                required: true
            },
            {
                name: "gender",
                component: "gender",
                placeholder: "מגדר",
                className: "_child__gender",
                required: true
            },


            // פרטי כיתת האם של התלמיד
            {
                ...affiliationField,
                name: "home_affiliation",
                className: "_child__school",
            },
            {
                name: "home_grade",
                placeholder: "שכבה",
                minWidth: 100,
                component: "autoComplete",
                className: "_child__grade",
                systemUI: true,
                options: grades,
                required: true
            },
            {
                name: "home_class",
                placeholder: "מספר כיתה",
                minWidth: 100,
                component: "autoComplete",
                className: "_child__t_class",
                systemUI: true,
                options: schoolClasses,
                required: true,
                isOptionEqualToValue: (option, value) => option.id === value.id
            },
            {
                name: "language",
                minWidth: 100,
                className: "_child__langauge",
                component: "autoComplete",
                options: languages,
                // multiple: true,
                labelKey: "label",
                systemUI: true,
                placeholder: "באיזו שפה ההורים מדברים בדרך כלל בבית?",
                isLocked: filled_fields?.languages, //DB יש לשים לב לגשת לשדות האלה בשם השמור ב
                // required: true,
                isOptionEqualToValue: (option, value) => option.id === value.id
            },
            {
                name: "parent_first_name",
                component: "input",
                placeholder: "שם הורה",
                className: "_parent__first_name",
                isLocked: filled_fields?.parent_first_name,
                // required: true
            },
            {
                name: "parent_last_name",
                component: "input",
                placeholder: " משפחה",
                className: "_parent__last_name",
                isLocked: filled_fields?.parent_last_name,
                // required: true
            },
            {
                name: "parent_phone",
                component: "input",
                className: "_parent__phone",
                placeholder: "טלפון לקבלת SMS",
                isLocked: filled_fields?.parent_phone,
            },
            {
                name: "parent_email",
                component: "input",
                className: "_parent__email",
                placeholder: "כתובת מייל",
                isLocked: filled_fields?.parent_email,
            }
        ], [schoolClasses, grades, schools, formValues]
    );

    const learningGroupFields = useMemo(() => [
        // {
        //     name: "learning_group",
        //     placeholder: "קבוצה",
        //     component: "autoComplete",
        //     options: [],
        //     optionsLimit: 20,
        //     systemUI: true,
        //     required: true,
        // }
    ], [])

    return { initialFields, learningGroupFields, fields }
}

export default useFields 